import { chakra, Text, TextProps } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  CONTENT_INNER_PX_SIZE,
  HEADER_ID,
  SIDEBAR_ID,
} from '@/components/templates/PageLayout';
import { deliveryServiceIdMap } from '@/domain/models/DeliveryService';
import { LIMIT_PER_PAGE } from '@/domain/models/Pagination';
import { PendingReason, StoreState } from '@/domain/models/Store';
import { useTargetsDomHeight, useTargetsDomWidth } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { LocalStorageHandler } from '@/lib/LocalStorageHandler';
import { ommisionText } from '@/lib/util';
import { StoreManegementPageLayout } from '@/pages/store-management/components/Layout/StoreManegementPageLayout';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import { useStoreManagementAuthStoreList } from '@/services/StoreManagement/AuthStores/hooks';
export const SEARCH_AREA_ID = 'search-form';
export const CardTitle: React.FC<TextProps> = chakra(Text, {
  baseStyle: {
    fontSize: 'lg',
    fontWeight: 'bold',
  },
});

import {
  CmBaseTable,
  CmBox,
  CmButton,
  CmCard,
  CmCheckBox,
  CmFlex,
  CmHeadingPagination,
  CmLabelAndInput,
  CmLabelAndMultipleReactSelect,
  CmLongLoading,
  CmMessageCard,
  CmScrollBox,
  CmTable,
  CmTableBodyRow,
  CmTableHeader,
  CmTbody,
  CmTd,
  CmText,
  CmThWithQuestionIcon,
  DownloadIcon,
  MultipleReactSelectInput,
  ReloadIcon,
  SelectForm,
  useCounter,
} from '@/components/uiParts';
import { DateDomainService } from '@/domain/services/Date/DateDomainService';
import {
  deliveryStoreForm,
  deliveryStoreTableThs,
} from '@/pages/store-management/delivery-stores-status-management/service';
import {
  DeliveryStore,
  useStoreManagementDeliveryStoreSearchList,
} from '@/services/StoreManagement/DeliveryStores/hooks/searchList/useStoreManagementDeliveryStoreSearchList';
import {
  useDeliveryStoresStatusCSVGenerationJobCreate,
  useDeliveryStoresStatusCSVGenerationJobGetPresignedURL,
  useDeliveryStoresStatusCSVGenerationJobLatest,
} from '@/services/StoreManagement/DeliveryStoresStatusCSVGenerationJob/hooks';
import { DeliveryStoresStatusCSVGenerationJobResponse } from '@/services/StoreManagement/DeliveryStoresStatusCSVGenerationJob/hooks/latest/useDeliveryStoresStatusCSVGenerationJobLatest';

const pendingStatus = 0;
const runningStatus = 1;
const successStatus = 2;
const errorStatus = 3;

const menuJobStatus = {
  [pendingStatus]: '待機中',
  [runningStatus]: '実行中',
  [successStatus]: '成功',
  [errorStatus]: '失敗',
} as const;

const StoreManagementDeliveryStoresStatusPage: React.FC =
  (): JSX.Element | null => {
    const history = useHistory();
    const { managerAccountMe } = useManagerAccountMe();
    // /* アクセス制御 */
    redirect404Page(
      'delivery_stores_status_management',
      history,
      managerAccountMe
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [deliveryStores, setDeliveryStores] = useState<DeliveryStore[]>([]);
    const { targetsHeight } = useTargetsDomHeight([HEADER_ID, SEARCH_AREA_ID]);
    const [storeName, setStoreName] = useState<string>('');
    const [timer, setTimer] = useState<number>();
    const { targetsWidth: sidebarWidth } = useTargetsDomWidth([SIDEBAR_ID]);
    const [searchCount, setSearchCount] = useState(0);
    const [searchAuthStores, setSearchAuthStores] = useState<SelectForm[]>([]);
    const [authStoreOptions, setAuthStoreOptions] = useState<SelectForm[]>([]);
    const [searchPendingReasons, setSearchPendingReasons] = useState<
      SelectForm[]
    >([]);
    const [pendingReasonOptions, setPendingReasonOptions] = useState<
      SelectForm[]
    >([]);
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [job, setJob] =
      useState<DeliveryStoresStatusCSVGenerationJobResponse | null>(null);
    const syncJobLatest = async () => {
      setIsLoading(true);
      const [{ delivery_stores, count }, job] = await Promise.all([
        useStoreManagementDeliveryStoreSearchList({
          ...getListQuery,
        }),
        useDeliveryStoresStatusCSVGenerationJobLatest(),
      ]);

      customizeCount(1);
      setSearchCount(count);
      setDeliveryStores(delivery_stores);
      setJob(job);
      setIsLoading(false);
    };
    const getPresignedURL = async () => {
      if (job === null) {
        return;
      }
      setIsLoading(true);
      try {
        // presignedURLを取得
        const res =
          await useDeliveryStoresStatusCSVGenerationJobGetPresignedURL(job.id);
        // ダウンロード用リンクを動的に作成してクリック
        const link = document.createElement('a');
        link.href = res.presigned_url; // 取得した署名付きURLをそのまま使用
        link.target = '_blank'; // 新しいタブで開く場合（必要に応じて削除）
        link.download = ''; // ファイル名はS3の設定に従う
        document.body.appendChild(link); // 一度DOMに追加しないとclick()が動作しなケースがあるため
        link.click();
        document.body.removeChild(link); // 作成したリンクを削除
      } catch (error) {
        console.error('failed getPresignedURL', error);
      } finally {
        setIsLoading(false);
      }
    };

    useEffect(() => {
      (async () => {
        setIsLoading(true);
        const { storeName, authStoreIds, storeState, pendingReasons } =
          LocalStorageHandler.get('deliveryStoreStatus');
        const [{ delivery_stores, count }, authStores, job] = await Promise.all(
          [
            useStoreManagementDeliveryStoreSearchList({
              ...getListQuery,
              storeName: storeName || '',
              authStoreIds: authStoreIds || [],
              storeState: storeState || '',
              pendingReasons: pendingReasons || [],
            }),
            useStoreManagementAuthStoreList(),
            useDeliveryStoresStatusCSVGenerationJobLatest(),
          ]
        );
        const authStoresOptions = authStores.map<SelectForm>((as) => ({
          value: as.id.toString(),
          label: as.name,
        }));
        const pendingReasonOptions = Object.entries(
          PendingReason
        ).map<SelectForm>(([k, v]) => ({
          value: k,
          label: v,
        }));
        setAuthStoreOptions(authStoresOptions);
        setPendingReasonOptions(pendingReasonOptions);
        setIsChecked(storeState === 'pending');
        setStoreName(storeName || '');
        setSearchAuthStores(
          authStoreIds?.map(
            (id) =>
              ({
                value: id.toString(),
                label: authStoresOptions.find(
                  (as) => as.value === id.toString()
                )?.label,
              } as SelectForm)
          ) || []
        );
        setSearchPendingReasons(
          pendingReasons?.map(
            (pr) =>
              ({
                value: pr.toString(),
                label: pendingReasonOptions.find((po) => po.value === pr)
                  ?.label,
              } as SelectForm)
          ) || []
        );
        customizeCount(1);
        setDeliveryStores(delivery_stores);
        setSearchCount(count);
        setJob(job);
        setIsLoading(false);
      })();
    }, []);

    const {
      count: currentPage,
      decrement,
      increment,
      customizeCount,
    } = useCounter(1);

    const handleInputName = (e: React.ChangeEvent<HTMLInputElement>) => {
      const storeName = e.target.value;
      setStoreName(storeName);
      clearTimeout(Number(timer));
      const newTimer = setTimeout(async () => {
        setIsLoading(true);
        const { delivery_stores, count } =
          await useStoreManagementDeliveryStoreSearchList({
            ...getListQuery,
            storeName,
          });
        customizeCount(1);
        setSearchCount(count);
        setDeliveryStores(delivery_stores);
        setIsLoading(false);
        LocalStorageHandler.set('deliveryStoreStatus', {
          storeName,
          authStoreIds: searchAuthStores.map((as) => Number(as.value)),
          storeState: isChecked ? 'pending' : '',
          pendingReasons: searchPendingReasons.map((pr) => pr.value),
        });
      }, 700);
      setTimer(Number(newTimer));
    };

    const searchAuthStoresSelectInput: MultipleReactSelectInput = {
      value: searchAuthStores,
      options: authStoreOptions,
      onChange: async (authStores) => {
        setIsLoading(true);
        setSearchAuthStores([...authStores]);
        const { delivery_stores, count } =
          await useStoreManagementDeliveryStoreSearchList({
            ...getListQuery,
            authStoreIds: authStores.map((as) => Number(as.value)),
          });
        customizeCount(1);
        setSearchCount(count);
        setDeliveryStores(delivery_stores), setIsLoading(false);
        LocalStorageHandler.set('deliveryStoreStatus', {
          storeName,
          authStoreIds: authStores.map((as) => Number(as.value)),
          storeState: isChecked ? 'pending' : '',
          pendingReasons: searchPendingReasons.map((pr) => pr.value),
        });
      },
      onReset: () => setSearchAuthStores([]),
    };

    const searchPendingReasonSelectInput: MultipleReactSelectInput = {
      value: searchPendingReasons,
      options: pendingReasonOptions,
      onChange: async (pendingReasons) => {
        setIsLoading(true);
        setSearchPendingReasons([...pendingReasons]);
        const { delivery_stores, count } =
          await useStoreManagementDeliveryStoreSearchList({
            ...getListQuery,
            pendingReasons: pendingReasons.map((pr) => pr.value),
          });
        customizeCount(1);
        setSearchCount(count);
        setDeliveryStores(delivery_stores), setIsLoading(false);
        LocalStorageHandler.set('deliveryStoreStatus', {
          storeName,
          authStoreIds: searchAuthStores.map((as) => Number(as.value)),
          storeState: isChecked ? 'pending' : '',
          pendingReasons: pendingReasons.map((pr) => pr.value),
        });
      },
      onReset: () => setSearchPendingReasons([]),
    };

    const searchIsPendingCheckboxInput = async (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      const isChecked = e.target.checked;
      const storeState = isChecked ? 'pending' : '';
      setIsLoading(true);
      setIsChecked(isChecked);
      const { delivery_stores, count } =
        await useStoreManagementDeliveryStoreSearchList({
          ...getListQuery,
          storeState,
        });
      customizeCount(1);
      setSearchCount(count);
      setDeliveryStores(delivery_stores), setIsLoading(false);
      LocalStorageHandler.set('deliveryStoreStatus', {
        storeName,
        authStoreIds: searchAuthStores.map((as) => Number(as.value)),
        storeState,
        pendingReasons: searchPendingReasons.map((pr) => pr.value),
      });
    };

    const toFirstPage = async () => {
      setIsLoading(true);
      const { delivery_stores } =
        await useStoreManagementDeliveryStoreSearchList({
          ...getListQuery,
        });
      setDeliveryStores(delivery_stores),
        customizeCount(1),
        setIsLoading(false);
    };

    const toPreviousPage = async () => {
      setIsLoading(true);
      const previousPage = currentPage - 1;
      const { delivery_stores } =
        await useStoreManagementDeliveryStoreSearchList({
          ...getListQuery,
          currentPage: previousPage,
        });
      setDeliveryStores(delivery_stores), decrement(), setIsLoading(false);
    };

    const toNextPage = async () => {
      setIsLoading(true);
      const nextPage = currentPage + 1;
      const { delivery_stores } =
        await useStoreManagementDeliveryStoreSearchList({
          ...getListQuery,
          currentPage: nextPage,
        });
      setDeliveryStores(delivery_stores), increment(), setIsLoading(false);
    };

    const toLastPage = async () => {
      setIsLoading(true);
      const lastPage = Math.ceil(searchCount / LIMIT_PER_PAGE);
      const { delivery_stores } =
        await useStoreManagementDeliveryStoreSearchList({
          ...getListQuery,
          currentPage: lastPage,
        });
      setDeliveryStores(delivery_stores),
        customizeCount(lastPage),
        setIsLoading(false);
    };

    const getListQuery = {
      currentPage: 1,
      limit: LIMIT_PER_PAGE,
      storeName,
      authStoreIds: searchAuthStores.map((as) => Number(as.value)),
      storeState: isChecked ? 'pending' : '',
      pendingReasons: searchPendingReasons.map((as) => String(as.value)),
    };

    const createJob = async () => {
      try {
        setIsLoading(true);
        await useDeliveryStoresStatusCSVGenerationJobCreate();
        // 最新ジョブを再fetch
        const newJob = await useDeliveryStoresStatusCSVGenerationJobLatest();
        setJob(newJob);
      } catch (error) {
        console.log('failed createJob', error);
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <StoreManegementPageLayout
        title={routes.deliveryStoresStatusManagement.title}
      >
        <CmBox>
          <CmBox mt={5} width="100%" textAlign="right">
            <CmButton onClick={createJob}>ステータスを更新</CmButton>
          </CmBox>
          <CmCard mt={2}>
            <CmFlex justifyContent="space-between" alignItems="center">
              <CardTitle fontSize="18px" mb={2}>
                ステータス更新状況
              </CardTitle>
            </CmFlex>
            <CmFlex justify="space-between" mt={4} alignItems="end">
              {job !== null ? (
                <CmBaseTable
                  ths={[
                    {
                      label: '更新状況',
                      minWidth: '200px',
                    },
                    {
                      label: '更新時刻',
                      minWidth: '200px',
                    },
                    {
                      label: 'CSVダウンロード',
                      minWidth: '200px',
                    },
                  ]}
                >
                  <CmTableBodyRow>
                    <CmTd textAlign="center">
                      <CmText
                        color={
                          job.status === successStatus
                            ? 'green'
                            : job.status === errorStatus
                            ? 'red'
                            : undefined
                        }
                      >
                        {menuJobStatus[job.status]}
                      </CmText>
                    </CmTd>
                    <CmTd textAlign="center">
                      {job.created_at !== null &&
                        DateDomainService.datetimeFormat(
                          new Date(job.created_at)
                        )}
                    </CmTd>
                    <CmTd textAlign="center">
                      {job !== null && job.status === successStatus && (
                        <DownloadIcon
                          width="25px"
                          height="25px"
                          cursor="pointer"
                          onClick={getPresignedURL}
                        />
                      )}
                    </CmTd>
                  </CmTableBodyRow>
                </CmBaseTable>
              ) : (
                <div />
              )}

              <CmFlex ml={'30px'}>
                {job !== null && (
                  <CmButton
                    mr={'25px'}
                    onClick={syncJobLatest}
                    variant="outline"
                  >
                    最新状況確認 <ReloadIcon ml="10px" />
                  </CmButton>
                )}
              </CmFlex>
            </CmFlex>
          </CmCard>

          <CmBox padding="20px 20px 20px 0" id={SEARCH_AREA_ID}>
            <CmFlex
              alignItems="flex-end"
              justifyContent="space-between"
              w="full"
            >
              <CmFlex width="full" justifyContent="space-between">
                <CmBox width="23%">
                  <CmLabelAndInput
                    bg="white"
                    id="name"
                    label={deliveryStoreForm.storeName.label}
                    fontWeight="bold"
                    value={storeName}
                    onChange={handleInputName}
                  />
                </CmBox>
                <CmBox width="23%">
                  <CmLabelAndMultipleReactSelect
                    isOptionScroll
                    label="拠点"
                    fontWeight="bold"
                    name="authStoreName"
                    selectInput={searchAuthStoresSelectInput}
                  />
                </CmBox>
                <CmBox width="23%">
                  <CmLabelAndMultipleReactSelect
                    isOptionScroll
                    label="停止理由"
                    fontWeight="bold"
                    name="authStoreName"
                    selectInput={searchPendingReasonSelectInput}
                  />
                </CmBox>
                <CmBox width="23%">
                  <CmCheckBox
                    size="md"
                    isChecked={isChecked}
                    onChange={searchIsPendingCheckboxInput}
                  >
                    注文停止中のみ表示
                  </CmCheckBox>
                </CmBox>
              </CmFlex>
            </CmFlex>
            <CmBox mt={5} width="100%" textAlign="right">
              件数: {searchCount} 件
            </CmBox>
          </CmBox>

          {deliveryStores.length > 0 ? (
            <CmCard
              mt={2}
              mb={5}
              padding="20px"
              height={`calc(100vh - ${targetsHeight}px - 20px)`}
              w={`calc(100vw - ${sidebarWidth + CONTENT_INNER_PX_SIZE * 2}px)`}
            >
              <CmFlex flexDirection="row-reverse" my="5px">
                <CmHeadingPagination
                  totalNum={searchCount}
                  LimitPerPage={LIMIT_PER_PAGE}
                  {...{
                    currentPage,
                    toFirstPage,
                    toNextPage,
                    toPreviousPage,
                    toLastPage,
                  }}
                />
              </CmFlex>
              <CmScrollBox>
                <CmTable>
                  <CmTableHeader>
                    {deliveryStoreTableThs.map((th, index) => {
                      const style = {
                        minWidth: th.minWidth,
                        left: index === 0 ? '0' : undefined,
                        zIndex: index === 0 ? '2' : undefined,
                      } as const;
                      return (
                        <CmThWithQuestionIcon
                          key={th.label}
                          {...style}
                          label={th.label}
                          question={th.question}
                        />
                      );
                    })}
                  </CmTableHeader>
                  <CmTbody>
                    {deliveryStores.map((deliveryStore, index) => {
                      const {
                        store_name,
                        store_state,
                        pending_reason,
                        delivery_service_id,
                        auth_store_name,
                        updated_at,
                      } = deliveryStore;
                      return (
                        <CmTableBodyRow key={index + 1}>
                          <CmTd>
                            {Object.entries(deliveryServiceIdMap).map(
                              ([key, value]) => {
                                if (value === delivery_service_id) {
                                  return key;
                                }
                              }
                            )}
                          </CmTd>
                          <CmTd textAlign="center">
                            {StoreState[store_state as keyof typeof StoreState]}
                          </CmTd>
                          <CmTd textAlign="center">
                            {
                              PendingReason[
                                pending_reason as keyof typeof PendingReason
                              ]
                            }
                          </CmTd>
                          <CmTd>{ommisionText(auth_store_name, 20)}</CmTd>
                          <CmTd>{ommisionText(store_name, 20)}</CmTd>
                          <CmTd>{updated_at}</CmTd>
                        </CmTableBodyRow>
                      );
                    })}
                  </CmTbody>
                </CmTable>
              </CmScrollBox>
            </CmCard>
          ) : (
            <>
              {!isLoading && (
                <CmMessageCard margin="100px auto 0">
                  検索結果に一致する店舗が見当たりませんでした。
                </CmMessageCard>
              )}
            </>
          )}
          {isLoading && <CmLongLoading />}
        </CmBox>
      </StoreManegementPageLayout>
    );
  };

export default StoreManagementDeliveryStoresStatusPage;
